/**
 * Auth token management.
 *
 * WEB: authentication uses httpOnly cookies. Tokens are never visible to JS, so
 * every function here is a no-op and getAuthToken() returns null (the request
 * interceptor then adds no header and the browser sends the cookie).
 *
 * NATIVE (Capacitor Android/iOS): the app's webview origin is cross-site to the
 * API, so the SameSite=Lax cookie is never sent. Instead the app logs in via
 * POST /api/auth/token, stores the returned JWT here, and the request interceptor
 * attaches it as `Authorization: Bearer`. The token is persisted with
 * @capacitor/preferences (native storage) so the session survives app restarts,
 * with an in-memory copy for synchronous reads on every request.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const TOKEN_KEY = 'vegetablah_auth_token';

const isNative = () => {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

// Synchronous cache so the axios request interceptor can read the token without
// awaiting native storage on every call. Kept in sync with Preferences below.
let inMemoryToken = null;

/** Current bearer token (native) or null (web / logged out). Synchronous. */
export const getAuthToken = () => inMemoryToken;

/** Store the bearer token after a native login. No-op on web. */
export const setAuthToken = async (token) => {
  inMemoryToken = token || null;
  if (!isNative()) return; // web uses httpOnly cookies
  if (token) {
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } else {
    await Preferences.remove({ key: TOKEN_KEY });
  }
};

/** Clear the stored bearer token (logout / 401). No-op on web. */
export const removeAuthToken = async () => {
  inMemoryToken = null;
  if (!isNative()) return;
  await Preferences.remove({ key: TOKEN_KEY });
};

/**
 * Load any persisted token into memory at app start. Must be awaited before the
 * first authenticated request on native, or that request goes out without the
 * header. No-op on web.
 * @returns {Promise<string|null>} the loaded token (or null)
 */
export const initializeAuthToken = async () => {
  if (isNative()) {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    inMemoryToken = value || null;
  }
  return inMemoryToken;
};
